/* eslint-disable react-hooks/exhaustive-deps */
import { useCallback, useEffect, useRef } from "react"
import { useSetRecoilState } from "recoil"
import { HIGH_VIDEO_STREAM_TYPE, LOW_VIDEO_STREAM_TYPE } from "../../constants"
import { MainVideoStreamTypeAtom } from "../../recoil/atoms/remoteVideoStreamType"
const { RemoteStatusPoor, RemoteStatusCritical } =
  window.MeetOnline.RTCNackStatus

/*
  - If the nackStatus is not poor or critical for 3sec then we set main video stream quality to low
  - If the nackStatus is not poor or critical for 60sec then we set main video stream quality to high and show toast and only one time we upscale the video quality
 */

const useMainVideoScaling = () => {
  const upScaleTimerId = useRef(null)
  const downScaleTimerId = useRef(null)
  const hasOnceScaleUp = useRef(false)
  const currentScaleValue = useRef(HIGH_VIDEO_STREAM_TYPE)
  const setMainVideoStreamType = useSetRecoilState(MainVideoStreamTypeAtom)

  const startUpScaleTimer = useCallback(() => {
    if (upScaleTimerId.current) {
      return
    }
    // Update scale is done if nack is good for 60 sec
    upScaleTimerId.current = setTimeout(() => {
      setMainVideoStreamType(HIGH_VIDEO_STREAM_TYPE)
      currentScaleValue.current = HIGH_VIDEO_STREAM_TYPE
      hasOnceScaleUp.current = true
    }, 60 * 1000) // 60sec
  }, [])

  const stopUpScaleTimer = useCallback(() => {
    if (upScaleTimerId.current) {
      clearTimeout(upScaleTimerId.current)
    }
    upScaleTimerId.current = null
  }, [])

  const startDownScaleTimer = useCallback(() => {
    if (downScaleTimerId.current) {
      return
    }
    downScaleTimerId.current = setTimeout(() => {
      setMainVideoStreamType(LOW_VIDEO_STREAM_TYPE)
      currentScaleValue.current = LOW_VIDEO_STREAM_TYPE
    }, 3 * 1000) // 3sec;
  }, [])

  const stopDownScaleTimer = useCallback(() => {
    if (downScaleTimerId.current) {
      clearTimeout(downScaleTimerId.current)
    }
    downScaleTimerId.current = null
  }, [])

  const reset = useCallback(() => {
    stopDownScaleTimer()
    stopUpScaleTimer()
  }, [])

  const handleNackStatusChange = useCallback((nackStatus: number) => {
    // set main video stream type to low if nack remote status is poor or critical
    if (
      nackStatus === RemoteStatusPoor ||
      nackStatus === RemoteStatusCritical
    ) {
      stopUpScaleTimer()
      if (currentScaleValue.current === HIGH_VIDEO_STREAM_TYPE) {
        startDownScaleTimer()
      }
    } else {
      stopDownScaleTimer()
      if (
        currentScaleValue.current === LOW_VIDEO_STREAM_TYPE &&
        !hasOnceScaleUp.current
      ) {
        startUpScaleTimer()
      }
    }
  }, [])

  useEffect(() => {
    return () => {
      reset()
    }
  })

  return {
    handleNackStatusChange,
    reset
  }
}

export default useMainVideoScaling
